import { getLocalToday } from "utils/Common/Date";
import { createElement } from "react";
import { type ReactElement } from "react";
import { pdf, type DocumentProps } from "@react-pdf/renderer";
import PreconPdf, {
  PreconPdfProps,
  AdminUser,
} from "Components/Common/PreconPdf/PreconPdf";
import { PreconFormValues } from "Pages/Admin/PreconAuthoring/types/precon.types";

const getAdminUser = (): AdminUser => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}") as AdminUser;
  } catch {
    return {};
  }
};

export const preconPdfFileName = (projectName?: string) =>
  `PreconReport-${projectName ?? "export"}-${getLocalToday()}.pdf`;

export const generatePreconPdfBlob = (values: PreconFormValues): Promise<Blob> => {
  const adminUser = getAdminUser();
  const props: PreconPdfProps = { values, adminUser };
  const el = createElement(PreconPdf, props) as ReactElement<DocumentProps>;
  return pdf(el).toBlob();
};

export const downloadPreconPdf = async (
  values: PreconFormValues,
): Promise<void> => {
  const blob = await generatePreconPdfBlob(values);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = preconPdfFileName(values.projectName);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/** Opens the generated PDF in a new browser tab for preview.
 *  Opens the tab synchronously (preserving the user gesture) then replaces
 *  its location once the blob is ready, so popup blockers don't fire.
 *  Falls back to a direct download if the tab was blocked or toBlob fails.
 */
export const previewPreconPdf = async (values: PreconFormValues): Promise<void> => {
  const tab = window.open("", "_blank");
  try {
    const blob = await generatePreconPdfBlob(values);
    const url = URL.createObjectURL(blob);
    if (tab && !tab.closed) {
      tab.location.href = url;
    } else {
      // popup was blocked — fall back to download
      const a = document.createElement("a");
      a.href = url;
      a.download = preconPdfFileName(values.projectName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  } catch (err) {
    tab?.close();
    // toBlob failed (common WASM/network issue in CRA) — try download instead
    await downloadPreconPdf(values);
    throw err;
  }
};
